/**
 * Invoice settings — the tenant-level enums shared by the API, the typed
 * clients and the web app.
 *
 * Lives in `shared` rather than in the api app for the same reason
 * `customer-classification.ts` does: the stored value is a stable lowercase
 * code, the human-facing label lives beside it, and every consumer derives
 * from one definition so a dropdown and a validator can never disagree.
 */

import { z } from 'zod';

/** Built once: `isInvoiceSenderEmail` runs on every form validation. */
const senderEmailSchema = z.string().email();

/** What the settings form and API say about an issuer email that is not one. */
export const INVOICE_SENDER_EMAIL_ERROR = 'Enter a valid email address';

/**
 * Whether a stored or typed issuer email can be an invoice email's From
 * address. One rule for the settings form, the settings API and the sender —
 * the sender re-checks because rows saved before the rule existed were free
 * text.
 */
export function isInvoiceSenderEmail(s: string): boolean {
  return senderEmailSchema.safeParse(s).success;
}

/**
 * Whether a retainer invoice is raised before or after the period it covers.
 *
 * `advance` is the default and matches how myStartUpCFO bills: the run on the
 * last day of June raises July's retainer, because the retainer is paid for
 * service not yet rendered. `arrears` exists because the flip is a one-word
 * business decision, not a code change — and because the two reference
 * invoices disagree with each other on it, so the answer has to be data.
 *
 * Time & materials is always arrears (you cannot bill hours nobody has worked
 * yet) and never consults this setting.
 */
export const RETAINER_INVOICE_MODES = ['advance', 'arrears'] as const;
export type RetainerInvoiceMode = (typeof RETAINER_INVOICE_MODES)[number];

export const RETAINER_INVOICE_MODE_LABELS: Record<RetainerInvoiceMode, string> = {
  advance: 'In advance (bill before the period)',
  arrears: 'In arrears (bill after the period)',
};

/** Type guard for reading the CHECK-constrained text column back off a row.
 *  Validate rather than blind-cast so a value that got past the constraint
 *  (constraint dropped, manual edit) falls back to a default instead of
 *  leaking an unsound type to clients. */
export function isRetainerInvoiceMode(v: unknown): v is RetainerInvoiceMode {
  return typeof v === 'string' && (RETAINER_INVOICE_MODES as readonly string[]).includes(v);
}

/** Standard payment terms. The label is stored and printed; the day count is
 * derived wherever a due date is needed, so settings cannot hold two values
 * that disagree. */
export const INVOICE_TERMS_PRESETS = [
  { label: 'Due on receipt', days: 0 },
  { label: 'Net 10', days: 10 },
  { label: 'Net 15', days: 15 },
  { label: 'Net 30', days: 30 },
  { label: 'Net 45', days: 45 },
  { label: 'Net 60', days: 60 },
] as const;
export type InvoiceTermsPreset = (typeof INVOICE_TERMS_PRESETS)[number];

/**
 * Plain "N days" terms read out of free text: `Net 30`, `net30`, `10 days`, a
 * bare `0`, `Due on receipt`. The label comes back in its canonical spelling —
 * `Net N`, or `Due on receipt` for zero — so a customer whose credit term was
 * typed `0` does not print a lone "0" in the invoice's Terms box.
 *
 * Null for anything else (`Net 30 EOM`, `2/10 Net 30`): those carry a meaning
 * a day count cannot hold, and guessing one would print a due date nobody
 * agreed to.
 */
export function parseInvoiceTerms(
  text: string | null | undefined,
): { label: string; days: number } | null {
  const t = (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (/^(due )?(on|upon) receipt$/.test(t)) return { label: 'Due on receipt', days: 0 };
  const m = /^(?:net ?)?(\d{1,3})(?: ?days?)?$/.exec(t);
  if (!m?.[1]) return null;
  const days = Number(m[1]);
  return { label: days === 0 ? 'Due on receipt' : `Net ${days}`, days };
}

/** The due-day offset represented by standard terms, or null for no terms or
 * a legacy/custom label whose meaning cannot be inferred safely. */
export function invoiceDueDaysForTerms(terms: string | null): number | null {
  return parseInvoiceTerms(terms)?.days ?? null;
}

/**
 * The Terms a new invoice for a customer prints, from the customer's free-text
 * credit term: the canonical label when it reads as plain terms, the text as
 * typed when it does not (it is still what was agreed — it just yields no due
 * date), and null when the customer has none, so the tenant default applies.
 */
export function invoiceTermsForCreditTerm(creditTerm: string | null | undefined): string | null {
  const raw = creditTerm?.trim() ?? '';
  if (raw === '') return null;
  return parseInvoiceTerms(raw)?.label ?? raw;
}
